import { ExternalLink } from "lucide-react";
import { Seo, Reveal } from "../components/Seo";
import { PageHero, CtaBand, Eyebrow } from "../components/blocks";
import { PARENT_SITE } from "../lib/site";

export default function OurGroup() {
  return (
    <>
      <Seo
        title="Our Group — Softline Nigeria & Softline Sweden"
        description="The relationship between Softline Technologies and Services AB (Sweden) and Softline Technologies and Services Nigeria Limited."
        path="/about/our-group"
      />
      <PageHero
        eyebrow="The Softline Group"
        title={["One group identity.", "Two national companies."]}
        lead="Softline operates as a group of legally distinct national companies sharing brand, engineering standards and a common technology mission."
        crumbs={[{ label: "Home", to: "/" }, { label: "About Us", to: "/about" }, { label: "Our Group" }]}
      />

      <section className="bg-white" data-testid="group-entities">
        <div className="shell py-24 md:py-32">
          <div className="grid lg:grid-cols-2 gap-6">
            <Reveal>
              <div className="h-full border border-charcoal/10 p-8 md:p-10" data-testid="group-entity-sweden">
                <Eyebrow>Parent company</Eyebrow>
                <h2 className="font-display font-bold text-2xl md:text-3xl mt-5">Softline Technologies and Services AB</h2>
                <p className="mt-2 font-mono text-xs uppercase tracking-[0.2em] text-charcoal/60">Sweden</p>
                <p className="mt-6 text-charcoal/75 leading-relaxed">
                  The Swedish parent company carries the group's international corporate presence, consultancy practice and
                  technology standards. It anchors Softline's engineering culture and group-level relationships.
                </p>
                <a
                  href={PARENT_SITE}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="group-entity-sweden-link"
                  className="mt-8 inline-flex items-center gap-2 bg-charcoal text-white font-semibold px-6 py-3.5 hover:bg-black transition-colors duration-200"
                >
                  Visit softlinetechs.com <ExternalLink size={15} />
                </a>
                <p className="mt-6 text-xs text-charcoal/50 leading-relaxed">
                  Legal notices, registration details and contact information for the Swedish entity are published on the
                  Swedish website and apply there only.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.12}>
              <div className="h-full bg-charcoal text-white p-8 md:p-10" data-testid="group-entity-nigeria">
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-lime">
                  <span className="inline-block h-2 w-2 bg-lime mr-3 align-middle" aria-hidden="true"></span>
                  Nigerian subsidiary
                </p>
                <h2 className="font-display font-bold text-2xl md:text-3xl mt-5">
                  Softline Technologies and Services Nigeria Limited
                </h2>
                <p className="mt-2 font-mono text-xs uppercase tracking-[0.2em] text-softgrey">Nigeria</p>
                <p className="mt-6 text-softgrey leading-relaxed">
                  The Nigerian company delivers proprietary digital products and enterprise technology services to the
                  Nigerian market, operating under group standards with Nigerian legal identity and local accountability.
                </p>
                <p className="mt-8 font-mono text-xs uppercase tracking-[0.2em] text-lime">softlinetechs.com.ng</p>
                <p className="mt-6 text-xs text-softgrey/60 leading-relaxed" data-testid="group-entity-nigeria-registration">
                  Registration number: RC 8778015. Offices: 35, Lake Chad, Maitama, Abuja; Abuja Conak Building,
                  17 Akinremi St, Anifowoshe, Ikeja, Lagos.
                </p>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.2}>
            <div className="mt-14 grid md:grid-cols-3 gap-px bg-charcoal/10 border border-charcoal/10" data-testid="group-shared-standards">
              {[
                {
                  t: "Shared brand, separate entities",
                  d: "Both companies trade under the Softline identity while remaining legally distinct — contracts, liabilities and registrations are national.",
                },
                {
                  t: "Shared engineering standards",
                  d: "Architecture principles, security practices and delivery discipline are aligned across the group.",
                },
                {
                  t: "Local delivery, local accountability",
                  d: "Nigerian engagements are contracted and delivered by the Nigerian entity, with group expertise behind them.",
                },
              ].map((c) => (
                <div key={c.t} className="bg-white p-7">
                  <h3 className="font-display font-bold text-lg">{c.t}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-charcoal/65">{c.d}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <CtaBand />
    </>
  );
}
